/**
 * 标签管理页 —— 对标「场景管理」的左树右列表布局。
 *
 * 左侧：标签树（目录 FolderOutlined / 标签叶子 TagOutlined），支持
 *       新建子目录/子标签、重命名、删除（右键或顶部按钮）。
 * 右侧：选中节点后的标签详情：
 *       - 标签信息卡（名称/路径/类型/创建人/创建时间/关联场景数）
 *       - 关联场景列表（目录取全部子标签并集），支持批量移除、清空、添加场景。
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Tree, Button, Input, Space, Modal, App, Spin, Empty, Table, Descriptions, Popconfirm, Tag, Tooltip } from 'antd';
import {
  PlusOutlined, FolderOutlined, TagOutlined, DeleteOutlined, EditOutlined,
  ClearOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import type { DataNode } from 'antd/es/tree';
import dayjs from 'dayjs';
import {
  getSceneTagTree, getSceneTagDetail, getSceneTagScenes,
  createSceneTag, renameSceneTag, deleteSceneTag,
  batchDeleteScenesFromTag, clearSceneTag,
  SceneTagNode, SceneTagDetail, SceneTagSceneItem, tagDisplayName,
} from '@apis/simulation';
import SceneSourceTag from '@components/SceneSourceTag';

const formatTime = (val: string | null | undefined) =>
  val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-';

function TagManagementPage() {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();

  const [tagTree, setTagTree] = useState<SceneTagNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedNode, setSelectedNode] = useState<SceneTagNode | null>(null);
  const [detail, setDetail] = useState<SceneTagDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [scenes, setScenes] = useState<SceneTagSceneItem[]>([]);
  const [scenesLoading, setScenesLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // 重命名/编辑弹窗
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<SceneTagNode | null>(null);
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [renameKey, setRenameKey] = useState('');
  const [renameAlias, setRenameAlias] = useState('');
  const [renameDesc, setRenameDesc] = useState('');
  // 新建弹窗（目录只输名称+描述;标签需英文键+别名+描述）
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createKey, setCreateKey] = useState('');
  const [createAlias, setCreateAlias] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createParent, setCreateParent] = useState<SceneTagNode | null>(null);
  const [createNodeFlag, setCreateNodeFlag] = useState(0);

  const findNode = (nodes: SceneTagNode[], id: number): SceneTagNode | null => {
    for (const n of nodes) {
      if (n.id === id) return n;
      const hit = findNode(n.children || [], id);
      if (hit) return hit;
    }
    return null;
  };

  const loadTree = useCallback(async (): Promise<SceneTagNode[]> => {
    setTreeLoading(true);
    try {
      const data = await getSceneTagTree();
      setTagTree(data);
      setExpandedKeys((prev) => (prev.length ? prev : data.map((n) => n.id)));
      return data;
    } catch (e) {
      console.error('加载标签树失败:', e);
      message.error('加载标签树失败');
      return [];
    } finally {
      setTreeLoading(false);
    }
  }, [message]);

  const loadDetail = useCallback(async (node: SceneTagNode, page = 1, pageSize = 20) => {
    setDetailLoading(true);
    setScenesLoading(true);
    try {
      const d = await getSceneTagDetail(node.id);
      setDetail(d);
      const { total, list } = await getSceneTagScenes([node.id], page, pageSize);
      setScenes(list);
      setPagination({ current: page, pageSize, total });
      setSelectedRowKeys([]);
    } catch (e) {
      console.error('加载标签详情失败:', e);
      message.error('加载标签详情失败');
    } finally {
      setDetailLoading(false);
      setScenesLoading(false);
    }
  }, [message]);

  useEffect(() => { void loadTree(); }, [loadTree]);

  const treeData = useMemo<DataNode[]>(() => tagTree.map(toDataNode), [tagTree]);

  function toDataNode(n: SceneTagNode): DataNode {
    return {
      key: n.id,
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {n.nodeFlag === 1
            ? <TagOutlined style={{ color: '#3b82f6' }} />
            : <FolderOutlined style={{ color: '#faad14' }} />}
          {tagDisplayName(n)}
        </span>
      ),
      children: (n.children && n.children.length > 0) ? n.children.map(toDataNode) : undefined,
    };
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 90 },
    {
      title: '场景名称',
      dataIndex: 'name',
      ellipsis: true,
      render: (v: string | null | undefined, r: SceneTagSceneItem) => (
        v ? <Link to={`/scene-annotate?sceneId=${r.id}`}>{v}</Link> : '-'
      ),
    },
    { title: '标签', dataIndex: 'label', width: 140, render: (v: string | null | undefined) => v || '-' },
    { title: '来源', dataIndex: 'source', width: 110, render: (v: string | null | undefined) => <SceneSourceTag source={v} /> },
    { title: '状态', dataIndex: 'statusName', width: 110, render: (v: string | null | undefined) => v || '-' },
    { title: '创建人', dataIndex: 'creator', width: 130, render: (v: string | null | undefined) => v || '-' },
    { title: '创建时间', dataIndex: 'createTime', width: 170, render: (v: string | null | undefined) => formatTime(v) },
  ];

  const onSelectNode = (keys: React.Key[]) => {
    const node = findNode(tagTree, Number(keys[0]));
    if (!node) return;
    setSelectedNode(node);
    void loadDetail(node);
  };

  const handleCreate = async () => {
    const isDir = createNodeFlag === 0;
    if (isDir && !createName.trim()) {
      message.warning('请输入名称');
      return;
    }
    if (!isDir && !createKey.trim()) {
      message.warning('请输入英文唯一键');
      return;
    }
    try {
      const base = {
        parentId: createParent ? createParent.id : -1,
        nodeFlag: createNodeFlag,
        description: createDesc.trim() || undefined,
      };
      await createSceneTag(
        isDir
          ? { ...base, name: createName.trim() }
          : { ...base, tagKey: createKey.trim(), alias: createAlias.trim() || undefined },
      );
      message.success('创建成功');
      setCreateOpen(false);
      setCreateName('');
      setCreateKey('');
      setCreateAlias('');
      setCreateDesc('');
      await loadTree();
    } catch (e) {
      console.error('创建标签失败:', e);
      message.error('创建失败');
    }
  };

  /** 打开重命名弹窗：先拉服务端最新详情再回填，避免使用陈旧的 selectedNode/tagTree 缓存
   *  （保存后树是异步刷新的，直接用本地缓存会出现「改完再点重命名还是旧值」）。 */
  const openRenameModal = useCallback(async (node: SceneTagNode) => {
    setRenameTarget(node);
    // 先用当前值占位，请求返回后覆盖
    setRenameName(node.name);
    setRenameKey(node.tagKey || '');
    setRenameAlias(node.alias || '');
    setRenameDesc(node.description || '');
    setRenameLoading(true);
    try {
      const d = await getSceneTagDetail(node.id);
      setRenameName(d.name);
      setRenameKey(d.tagKey || '');
      setRenameAlias(d.alias || '');
      setRenameDesc(d.description || '');
      // 同步修正本地 selectedNode，避免详情卡/后续操作继续用旧值
      setSelectedNode((prev) => (prev && prev.id === node.id
        ? { ...prev, name: d.name, tagKey: d.tagKey, alias: d.alias, description: d.description }
        : prev));
    } catch (e) {
      console.error('加载标签最新信息失败:', e);
      // 拉取失败退回当前缓存值，仍可编辑
    } finally {
      setRenameLoading(false);
      setRenameOpen(true);
    }
  }, []);

  const handleRename = async () => {
    if (!renameTarget) return;
    const isLeaf = renameTarget.nodeFlag === 1;
    if (isLeaf && !renameKey.trim()) { message.warning('请输入英文唯一键'); return; }
    if (!isLeaf && !renameName.trim()) { message.warning('请输入名称'); return; }
    try {
      const opts: Record<string, unknown> = {
        description: renameDesc.trim() || undefined,
        alias: renameAlias.trim() || undefined,
      };
      if (isLeaf) opts.tagKey = renameKey.trim();
      await renameSceneTag(renameTarget.id, isLeaf ? undefined : renameName.trim(), opts);
      message.success('保存成功');
      setRenameOpen(false);
      // 用接口返回的新树刷新选中节点（原实现读的是异步 setState 前的旧 tagTree）
      const freshTree = await loadTree();
      if (selectedNode && selectedNode.id === renameTarget.id) {
        const fresh = findNode(freshTree, renameTarget.id);
        if (fresh) { setSelectedNode(fresh); void loadDetail(fresh, 1); }
      }
    } catch (e) {
      console.error('保存失败:', e);
      message.error('保存失败');
    }
  };

  const handleDelete = async (node: SceneTagNode) => {
    try {
      await deleteSceneTag(node.id);
      message.success('已删除');
      if (selectedNode && selectedNode.id === node.id) {
        setSelectedNode(null);
        setDetail(null);
        setScenes([]);
      }
      await loadTree();
    } catch (e) {
      console.error('删除失败:', e);
      message.error('删除失败（有关联场景需先清空）');
    }
  };

  const handleBatchRemove = async () => {
    if (!selectedNode || selectedRowKeys.length === 0) return;
    try {
      const res = await batchDeleteScenesFromTag(selectedNode.id, selectedRowKeys.map(Number));
      message.success(`已移除 ${res.deleted} 个场景`);
      setSelectedRowKeys([]);
      await loadDetail(selectedNode);
    } catch (e) {
      console.error('移除场景失败:', e);
      message.error('移除场景失败');
    }
  };

  const handleClear = () => {
    if (!selectedNode) return;
    modal.confirm({
      title: `清空「${tagDisplayName(selectedNode)}」下的全部场景关联？`,
      content: '只移除标签关联，不删除场景本身。',
      okText: '清空',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await clearSceneTag(selectedNode.id);
          message.success('已清空');
          await loadDetail(selectedNode);
        } catch (e) {
          console.error('清空失败:', e);
          message.error('清空失败');
        }
      },
    });
  };

  return (
    <div style={{
      display: 'flex',
      height: 'calc(100dvh - 48px)',
      padding: 16,
      gap: 16,
      boxSizing: 'border-box',
    }}>
      {/* 左侧标签树 */}
      <div style={{
        width: 300, minWidth: 260, flexShrink: 0,
        background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>标签树</span>
          <Space size={4}>
            <Button
              size="small" type="primary" icon={<PlusOutlined />}
              onClick={() => { setCreateParent(null); setCreateNodeFlag(0); setCreateOpen(true); }}
            >
              新建
            </Button>
            <Button size="small" icon={<ReloadOutlined />} onClick={() => void loadTree()} />
          </Space>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
          {treeLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          ) : tagTree.length === 0 ? (
            <Empty description="暂无标签" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '40px 0' }} />
          ) : (
            <Tree
              treeData={treeData}
              selectedKeys={selectedNode ? [selectedNode.id] : []}
              expandedKeys={expandedKeys}
              onExpand={(keys) => setExpandedKeys(keys)}
              onSelect={onSelectNode}
              blockNode
              showLine={{ showLeafIcon: false }}
              style={{ padding: '0 8px' }}
            />
          )}
        </div>
      </div>

      {/* 右侧详情 */}
      <div style={{
        flex: 1, background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {!selectedNode ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8b95a5' }}>
            点击左侧标签查看标签详情与关联场景
          </div>
        ) : (
          <>
            {/* 标签信息卡 */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <Space size={8}>
                  {selectedNode.nodeFlag === 1
                    ? <Tag color="blue" icon={<TagOutlined />}>标签</Tag>
                    : <Tag color="gold" icon={<FolderOutlined />}>目录</Tag>}
                  <span style={{ fontSize: 16, fontWeight: 600 }}>{tagDisplayName(selectedNode)}</span>
                  <span style={{ color: '#8b95a5', fontSize: 13 }}>（ID: {selectedNode.id}）</span>
                </Space>
                <Space size={4}>
                  <Button
                    size="small" icon={<PlusOutlined />}
                    disabled={selectedNode.nodeFlag === 1}
                    onClick={() => { setCreateParent(selectedNode); setCreateNodeFlag(0); setCreateOpen(true); }}
                  >
                    新建子目录
                  </Button>
                  <Button
                    size="small" icon={<PlusOutlined />}
                    disabled={selectedNode.nodeFlag === 1}
                    onClick={() => { setCreateParent(selectedNode); setCreateNodeFlag(1); setCreateOpen(true); }}
                  >
                    新建子标签
                  </Button>
                  <Button size="small" icon={<EditOutlined />} loading={renameLoading} onClick={() => void openRenameModal(selectedNode)}>
                    重命名
                  </Button>
                  <Popconfirm
                    title={selectedNode.nodeFlag === 1 ? '删除该标签？' : '删除该目录（含全部子标签）？'}
                    description="有场景关联时后端会拒绝删除。"
                    okText="删除"
                    okButtonProps={{ danger: true }}
                    cancelText="取消"
                    onConfirm={() => void handleDelete(selectedNode)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
                  </Popconfirm>
                </Space>
              </div>
              <div style={{ marginTop: 12 }}>
                <Spin spinning={detailLoading}>
                <Descriptions size="small" column={3}>
                  <Descriptions.Item label="路径">{detail?.tagPath || '-'}</Descriptions.Item>
                  <Descriptions.Item label="英文键">{detail?.tagKey || '-'}</Descriptions.Item>
                  <Descriptions.Item label="中文别名">{detail?.alias || '-'}</Descriptions.Item>
                  <Descriptions.Item label="标签描述" span={3}>{detail?.description || '-'}</Descriptions.Item>
                  <Descriptions.Item label="创建人">{detail?.creator || '-'}</Descriptions.Item>
                  <Descriptions.Item label="创建时间">{formatTime(detail?.createTime)}</Descriptions.Item>
                  <Descriptions.Item label="关联场景">
                    {detail?.nodeFlag === 0 ? <span>{detail?.sceneCount ?? 0}（含全部子标签并集）</span> : (detail?.sceneCount ?? 0)}
                  </Descriptions.Item>
                </Descriptions>
                </Spin>
              </div>
            </div>
            {/* 关联场景列表 */}
            <div style={{
              flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: 16,
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12,
              }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>
                  关联场景（{pagination.total}）
                </span>
                <Space size={4}>
                  {selectedRowKeys.length > 0 && (
                    <Button size="small" danger onClick={() => void handleBatchRemove()}>
                      批量移除（{selectedRowKeys.length}）
                    </Button>
                  )}
                  <Button size="small" icon={<ClearOutlined />} danger onClick={handleClear}>清空</Button>
                  {/* 目录只是标签的组织形式,不能作为场景归属:仅叶子标签可添加场景 */}
                  <Tooltip title={selectedNode.nodeFlag === 0 ? '目录仅作分组，不能直接挂场景；请在其下选择具体标签后添加' : ''}>
                    <Button
                      size="small" type="primary" icon={<PlusOutlined />}
                      disabled={selectedNode.nodeFlag === 0}
                      onClick={() => navigate(`/add-scenes-to-tag/${selectedNode.id}?name=${encodeURIComponent(tagDisplayName(selectedNode))}`)}
                    >
                      添加场景
                    </Button>
                  </Tooltip>
                </Space>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Table
                  rowKey="id"
                  size="small"
                  loading={scenesLoading}
                  dataSource={scenes}
                  columns={columns}
                  rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                  }}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: false,
                    showTotal: (t) => `共 ${t} 个`,
                    onChange: (page, pageSize) => { if (selectedNode) { void loadDetail(selectedNode, page, pageSize); } },
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* 新建弹窗 */}
      <Modal
        open={createOpen}
        title={createParent ? `在「${tagDisplayName(createParent)}」下新建` : '新建根节点'}
        okText="创建"
        cancelText="取消"
        onOk={() => void handleCreate()}
        onCancel={() => { setCreateOpen(false); setCreateName(''); setCreateKey(''); setCreateAlias(''); setCreateDesc(''); }}
      >
        <div style={{ marginBottom: 12 }}>
          <span style={{ fontSize: 13 }}>类型：</span>
          <Space>
            <Button size="small" type={createNodeFlag === 0 ? 'primary' : 'default'} onClick={() => setCreateNodeFlag(0)}>目录</Button>
            <Button size="small" type={createNodeFlag === 1 ? 'primary' : 'default'} onClick={() => setCreateNodeFlag(1)}>标签</Button>
          </Space>
        </div>
        {createNodeFlag === 0 ? (
          // 目录:只需要名称 + 描述
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 13, color: '#1d1d1f', fontWeight: 500, marginBottom: 4 }}>
              <span style={{ color: '#ff4d4f', marginRight: 2 }}>*</span>名称
            </div>
            <Input
              placeholder="如 感知 / TLD"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              onPressEnter={() => void handleCreate()}
              autoFocus
            />
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: '#1d1d1f', fontWeight: 500, marginBottom: 4 }}>
                <span style={{ color: '#ff4d4f', marginRight: 2 }}>*</span>英文唯一键
              </div>
              <Input
                placeholder="如 green_red"
                value={createKey}
                onChange={(e) => setCreateKey(e.target.value)}
                onPressEnter={() => void handleCreate()}
                autoFocus
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: '#1d1d1f', fontWeight: 500, marginBottom: 4 }}>中文别名</div>
              <Input
                placeholder="如 绿红"
                value={createAlias}
                onChange={(e) => setCreateAlias(e.target.value)}
              />
            </div>
          </>
        )}
        <div>
          <div style={{ fontSize: 13, color: '#1d1d1f', fontWeight: 500, marginBottom: 4 }}>描述</div>
          <Input.TextArea
            placeholder="补充说明该标签/目录的使用场景（可选）"
            value={createDesc}
            onChange={(e) => setCreateDesc(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
        </div>
      </Modal>

      {/* 重命名/编辑弹窗 */}
      <Modal
        open={renameOpen}
        title={`编辑「${renameTarget ? tagDisplayName(renameTarget) : ''}」`}
        okText="保存"
        cancelText="取消"
        onOk={() => void handleRename()}
        onCancel={() => setRenameOpen(false)}
      >
        {renameTarget?.nodeFlag === 0 ? (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 13, color: '#1d1d1f', fontWeight: 500, marginBottom: 4 }}>
              <span style={{ color: '#ff4d4f', marginRight: 2 }}>*</span>名称
            </div>
            <Input
              placeholder="如 感知 / TLD"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onPressEnter={() => void handleRename()}
            />
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: '#1d1d1f', fontWeight: 500, marginBottom: 4 }}>
                <span style={{ color: '#ff4d4f', marginRight: 2 }}>*</span>英文唯一键
              </div>
              <Input
                placeholder="如 green_red"
                value={renameKey}
                onChange={(e) => setRenameKey(e.target.value)}
                onPressEnter={() => void handleRename()}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: '#1d1d1f', fontWeight: 500, marginBottom: 4 }}>中文别名</div>
              <Input
                placeholder="如 绿红"
                value={renameAlias}
                onChange={(e) => setRenameAlias(e.target.value)}
              />
            </div>
          </>
        )}
        <div>
          <div style={{ fontSize: 13, color: '#1d1d1f', fontWeight: 500, marginBottom: 4 }}>描述</div>
          <Input.TextArea
            placeholder="补充说明该标签/目录的使用场景（可选）"
            value={renameDesc}
            onChange={(e) => setRenameDesc(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
        </div>
      </Modal>
    </div>
  );
}

export default TagManagementPage;